// app/src/Router.jsx — hash-based router for Kanjo
// Routes mounted under #/path; deep links work because index.html is the only entry.
// Stubs exist for routes not yet built so the navigation graph is complete from day one.

function StubPage({ title, subtitle, tab = 'today' }) {
  return (
    <KShell tab={tab}>
      <KHeader back title="Coming soon" />
      <Empty
        icon={
          <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="6" width="16" height="13" rx="2"/>
            <path d="M3 10h16M7 14h4"/>
          </svg>
        }
        title={title}
        body={subtitle}
      />
    </KShell>
  );
}

// Rendered when a free-tier user navigates to a paid-only route (typed
// URL, deep link, bookmark). Maintenance + Reports + Account stay free —
// see freeTier.canAccessRoute for the allowlist.
function UpgradePage({ tab = 'today' }) {
  return (
    <KShell tab={tab}>
      <KHeader back title="Upgrade to Basic" />
      <div style={{ padding: '0 20px', maxWidth: 480, margin: '0 auto' }}>
        <div className="card" style={{ padding: 22 }}>
          <div className="label" style={{ marginBottom: 8 }}>Paid feature</div>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--ink)', marginBottom: 8, letterSpacing: '-0.01em' }}>
            This screen is part of the full Kanjo Solution
          </h2>
          <p style={{ fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.55, marginBottom: 16 }}>
            Your free account includes Kanjo Contacts and the Census market tile. Cashflow, COGS, vendor spend, receipt scanning and the rest are part of the Basic / Pro plans.
          </p>
          <button onClick={() => window.location.hash = '/account'} className="btn primary" style={{ width: '100%' }}>
            Open Account to upgrade
          </button>
        </div>
      </div>
    </KShell>
  );
}

// ROUTES — paths must start with '/' and are matched exactly.
// Adding a route: drop a new key + render fn. Adding params (e.g. /buy/vendor/:id)
// requires a small upgrade to the matcher; intentionally left simple for now.
const ROUTES = {
  '/today':                  () => <Today />,

  '/receipts':               () => <ReceiptsMenu />,
  '/receipts/scan':            () => <ScanCapture />,
  '/receipts/scan/upload':     () => <ScanCapture />,   // legacy deep link — same screen
  '/receipts/scan/processing': () => <ScanProcessing />,
  '/receipts/scan/confirm':    () => <ScanConfirm />,
  '/receipts/scan/done':       () => <ScanDone />,
  '/receipts/manual':        () => <ManualEntry />,
  '/receipts/recent':        () => <RecentReceipts />,
  '/receipts/detail':        () => <InvoiceDetail />,
  '/receipts/bills':         () => <BillsDue />,

  '/buy':                    () => <BuyMenu />,
  '/buy/list':               () => <ShoppingList />,
  '/buy/order':              () => <OrderGuide />,
  '/buy/compare':            () => <PriceCompare />,
  '/buy/price-check':        () => <PriceCheck />,
  '/buy/vendor':             () => <VendorList />,
  '/buy/vendor/detail':      () => <VendorDetail />,
  '/buy/new-vendor':         () => <NewVendor />,

  '/maintenance':            () => <MaintenanceMenu />,
  '/maintenance/add':        () => <MaintenanceAdd />,
  '/maintenance/detail':     () => <MaintenanceDetail />,

  '/spending':               () => <SpendingScreen />,

  '/delivery':               () => <DeliveryMenu />,
  '/delivery/upload':        () => <DeliveryUpload />,
  '/delivery/detail':        () => <DeliveryDetail />,

  '/reports':                () => <ReportsMenu />,
  '/reports/sales':          () => <SalesClose />,
  '/reports/sales-trends':   () => <SalesTrends />,
  '/reports/cogs':           () => <COGSDashboard />,
  '/reports/cash':           () => <CashflowDashboard />,
  '/reports/vendor-spend':   () => <VendorSpend />,
  '/reports/price-changes':  () => <PriceChanges />,
  '/reports/taxes':          () => <TaxReport />,
  '/reports/payroll':        () => <PayrollReport />,
  '/reports/supplies':       () => <SuppliesReport />,
  '/reports/rent':           () => <RentReport />,
  '/reports/insurance':      () => <InsuranceReport />,
  '/reports/capex':          () => <CapexReport />,
  '/reports/census':         () => <CensusReport />,
  '/reports/tips':           () => <StubPage tab="reports" title="Tips & shifts" subtitle="Labor hours, tip pool, and per-staff distribution for the pay period." />,
  '/reports/matches':        () => <StubPage tab="reports" title="3-way matches" subtitle="PO ↔ delivery slip ↔ invoice, line-by-line. Resolve shorts and substitutions." />,

  '/help':                   () => <HelpScreen />,

  '/admin':                  () => <AdminDashboard />,
  '/admin/mailbox':          () => <MailboxScreen />,
  '/admin/funnel':           () => <FunnelDashboard />,
  '/account':                () => <AccountScreen />,
  '/account/plan':           () => <SubscribeScreen />, // retired Change-plan screen → billing hub
  '/account/subscribe':      () => <SubscribeScreen />,
  '/account/team':           () => <TeamScreen />,
};

function Router({ route, tier }) {
  // Strip query string (?id=...) before matching — components read the query
  // themselves via window.location.hash.
  const path = (route || '').split('?')[0];
  // Free-tier gate (Phase 3): if the operator's tier is 'free' and the
  // requested route isn't on the allowlist, render an upgrade card
  // instead of the feature. The allowlist is in freeTier.canAccessRoute.
  // `tier` arrives via prop from main.jsx; null/undefined means "still
  // loading", in which case we render normally (paid behavior — no
  // flash of upgrade card for paid users).
  const canAccess = window.kanjo?.scale?.freeTier?.canAccessRoute;
  if (tier != null && canAccess && !canAccess(path, tier)) {
    return <UpgradePage />;
  }
  const r = ROUTES[path];
  if (r) return r();
  // Unknown route → Today (paid) or upgrade card (free)
  if (tier != null && canAccess && !canAccess('/today', tier)) {
    return <UpgradePage />;
  }
  return <Today />;
}

Object.assign(window, { Router, ROUTES, StubPage, UpgradePage });
